import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import type { ReactNode } from 'react';

import { useLayoutOrder } from '@/hooks/useLayoutOrder';
import cn from '@/utils/cn';
import { restrictToParentElement } from '@/utils/dndModifiers';

export interface SortableItem {
  id: string;
  node: ReactNode;
  colSpanClass?: string;
}

interface SortableRowProps {
  storageKey: string;
  items: SortableItem[];
  gridClass: string;
}

export function SortableRow({ storageKey, items, gridClass }: SortableRowProps) {
  const defaultIds = items.map((i) => i.id);
  const { order, save } = useLayoutOrder(storageKey, defaultIds);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const byId = new Map(items.map((i) => [i.id, i] as const));
  const ordered = order.map((id) => byId.get(id)).filter((it): it is SortableItem => Boolean(it));

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = order.indexOf(String(active.id));
    const newIdx = order.indexOf(String(over.id));
    if (oldIdx < 0 || newIdx < 0) return;
    save(arrayMove(order, oldIdx, newIdx));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToParentElement]}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={order} strategy={rectSortingStrategy}>
        <div className={cn('grid gap-3', gridClass)}>
          {ordered.map((it) => (
            <SortableCell key={it.id} id={it.id} colSpanClass={it.colSpanClass}>
              {it.node}
            </SortableCell>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

interface SortableCellProps {
  id: string;
  colSpanClass?: string;
  children: ReactNode;
}

function SortableCell({ id, colSpanClass, children }: SortableCellProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'group/sortable relative h-full',
        isDragging && 'rounded-xl opacity-80 shadow-lg ring-2 ring-primary/30',
        colSpanClass,
      )}
    >
      <button
        type="button"
        aria-label="Drag to reorder"
        // Footprint contract with every card that reserves a header gutter for
        // this handle (PanelCard, MisCardShell, MfdSectionCard, ...): the
        // reserved right padding must be >= offset + size.
        //   below md: right-1 (4px) + h-11/w-11 (44px) = 48px -> pr-12
        //   md and up: right-2 (8px) + h-6/w-6 (24px)  = 32px -> pr-10
        // 44px below md is the touch-target floor; on md+ the handle is a
        // hover-revealed 24px affordance and stays out of the way.
        className={cn(
          'absolute right-1 top-1 z-10 inline-flex h-11 w-11 cursor-grab touch-none items-center justify-center rounded-md md:right-2 md:top-2 md:h-6 md:w-6',
          'bg-background/80 text-muted-foreground opacity-100 backdrop-blur transition hover:bg-secondary hover:text-foreground md:opacity-0',
          // focus-visible:ring is load-bearing, not decoration. The grip is
          // invisible until hover, so a keyboard user tabbing to it saw it
          // simply appear with nothing marking it as focused - and this is the
          // reorder affordance for every panel on the board.
          'md:group-hover/sortable:opacity-100 focus-visible:opacity-100 active:cursor-grabbing',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        )}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4 md:h-3.5 md:w-3.5" />
      </button>
      <div className="h-full">{children}</div>
    </div>
  );
}
